import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n";
import ChapterHeading from "./ChapterHeading";
import { expoImages } from "../data";

export default function Expo() {
  const { t } = useLang();
  const s = t.expo;
  return (
    <section id="expo" data-testid="expo-section" className="py-32 md:py-48 bg-ink text-linen">
      <ChapterHeading num={s.num} title={s.title} note={s.note} testid="expo-heading" />
      {s.intro && (
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          data-testid="expo-intro"
          className="px-5 md:px-10 max-w-3xl font-serif font-light text-xl md:text-3xl leading-snug text-linen/80 mb-16 md:mb-24 -mt-8 md:-mt-12"
        >
          {s.intro}
        </motion.p>
      )}
      <div className="px-5 md:px-10 border-t border-linen/15">
        {s.items.map((item, i) => (
          <motion.div
            key={i}
            data-testid={`expo-row-${i}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="group grid grid-cols-12 items-baseline gap-4 py-8 md:py-12 border-b border-linen/15"
          >
            <span className="col-span-3 md:col-span-2 font-mono text-sm tracking-[0.25em] text-linen/50 group-hover:text-terra transition-colors duration-300">
              {item.year}
            </span>
            <h3 className="col-span-9 md:col-span-6 font-serif text-2xl md:text-5xl tracking-tight leading-none group-hover:translate-x-3 transition-[transform] duration-500">
              {item.title}
            </h3>
            <span className="col-span-9 col-start-4 md:col-span-3 md:col-start-auto text-xs md:text-sm text-linen/60">
              {item.place}
            </span>
            <span className="hidden md:flex col-span-1 justify-end items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-linen/50">
              {item.status === "upcoming" ? s.upcoming : s.past}
              <ArrowUpRight size={14} className="opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-[opacity,transform] duration-300" />
            </span>
          </motion.div>
        ))}
      </div>
      <div data-testid="expo-photos" className="px-5 md:px-10 mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-6">
        {expoImages.map((item, i) => (
          <motion.div
            key={i}
            data-testid={`expo-photo-${i}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className={`group relative overflow-hidden border border-linen/15 ${item.ratio} ${item.className}`}
          >
            <img
              src={item.img}
              alt="Huit-Sept — Limoges, 2016"
              loading="lazy"
              className="h-full w-full object-cover grayscale-[35%] transition-[filter,transform] duration-700 ease-out group-hover:grayscale-0 group-hover:scale-105"
            />
          </motion.div>
        ))}
      </div>
      <p className="px-5 md:px-10 mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-linen/40">
        Huit-Sept — Limoges, 2016
      </p>
    </section>
  );
}
